import { FC, useState } from 'react';
import dayjs from 'dayjs';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportTaxByCustomerStateData,
  TReportTaxByCustomerStateParams,
} from '@src/types/reports';
import {
  exportTaxByCustomerState,
  getTaxByCustomerState,
} from '@src/services/reports';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import { useCountryOptions } from '@hooks/useCountryOptions';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

export const TaxByCustomerState: FC = () => {
  const [data, setData] = useState<TReportTaxByCustomerStateData[]>([]);
  const { countryOptions } = useCountryOptions();

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
    enableCountries: true,
  });

  const { columns, displayItems, tableData } =
    useReportTableColumns<TReportTaxByCustomerStateData>({
      data,
      selectedItems: [],
      compareToPreviousPeriod: filters.compareToPreviousPeriod,
      compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
      getAccessField: (item) => item.date,
      getMainDataField: (item, key) => item.states[key] || 0,
      getPreviousPeriodField: (item, key) => item.states_previous?.[key] || 0,
      getPreviousYearField: (item, key) =>
        item.states_previous_year?.[key] || 0,
      itemsField: 'states',
      columnTitleFormatter: (key) => key.toUpperCase(),
      valueFormatter: (_, value) => `$${value.toLocaleString()}`,
      includeTotalsRow: true,
    });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.states[key] || 0,
    getPreviousPeriodField: (item, key) => item.states_previous?.[key],
    getPreviousYearField: (item, key) => item.states_previous_year?.[key],
  });

  const buildParams = (): TReportTaxByCustomerStateParams => ({
    start_date: filters.startDate
      ? dayjs(filters.startDate).format('YYYY-MM-DD')
      : null,
    end_date: filters.endDate
      ? dayjs(filters.endDate).format('YYYY-MM-DD')
      : null,
    group: filters.selectedGroupBy.toLowerCase() as ReportGroupBy,
    country:
      filters.selectedCountries.length > 0
        ? filters.selectedCountries[0]
        : 'US',
    compare_to_previous_period: filters.compareToPreviousPeriod,
    compare_to_same_period_previous_year:
      filters.compareToSamePeriodPreviousYear,
  });

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const response = await getTaxByCustomerState(buildParams());

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportCsv = () => {
    filters.setIsLoading(true);
    exportTaxByCustomerState(buildParams(), 'csv')
      .then((response) => {
        const csvBlob = new Blob([response.data], {
          type: 'text/csv;charset=utf-8;',
        });
        const csvUrl = URL.createObjectURL(csvBlob);
        const link = document.createElement('a');

        link.href = csvUrl;
        link.download = 'tax_by_customer_state.csv';
        document.body.appendChild(link);
        link.click();
        URL.revokeObjectURL(csvUrl);
        document.body.removeChild(link);
        toast.success('Report exported successfully!');
      })
      .catch(() => toast.error('Failed to export report'))
      .finally(() => filters.setIsLoading(false));
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Tax By Customer State Report
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          showCountriesFilter
          showGroupByFilter
          showPeriodFilter
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          countryOptions={countryOptions}
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedCountries={filters.selectedCountries}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onCountriesChange={filters.setSelectedCountries}
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #3D3D3D',
                  borderRadius: '8px',
                  boxShadow: '0px 7px 13px 0px #B6D72C14',
                }}
                formatter={(value: number) => `$${value.toLocaleString()}`}
              />
              {displayItems.map((state, index) => (
                <Line
                  key={state}
                  activeDot={{ r: 6 }}
                  dataKey={state}
                  dot={{ r: 4 }}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}
              {filters.compareToPreviousPeriod &&
                displayItems.map((state, index) => (
                  <Line
                    key={`${state}_previous_period`}
                    dataKey={`${state}_previous_period`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((state, index) => (
                  <Line
                    key={`${state}_previous_year`}
                    dataKey={`${state}_previous_year`}
                    dot={{ r: 3 }}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={2}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={handleExportCsv}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
